/**
 * Coding Run Humanizer
 *
 * Shared, React-free logic that turns raw space_task_losi_run_events rows
 * into clean, human-readable activity lines with an icon kind. Extracted
 * from components/spaces/coding/task-coding-panel.tsx so both the Spaces
 * task panel and the chat Losi Code canvas render the same live timeline.
 *
 * Primary responsibilities:
 * - Humanize a single runner timeline entry (lifecycle, text, or Claude
 *   stream JSON) into a short line + icon kind
 * - Collapse a raw event stream into de-duplicated activity lines
 *
 * Dependencies:
 * - none (pure functions, safe on server and client)
 */

export type CodingActivityIcon =
  | "edit"
  | "write"
  | "read"
  | "command"
  | "search"
  | "web"
  | "plan"
  | "task"
  | "tool"
  | "text"
  | "repo"
  | "branch"
  | "start"
  | "check"
  | "error"
  // Diff/plain line kinds: rendered WITHOUT an icon. diffAdd = green, diffRemove
  | "diffAdd"
  | "diffRemove"
  | "plain"

export type CodingHumanizedEntry = {
  text: string
  icon: CodingActivityIcon
} | null

/** A raw timeline row (subset used for humanizing). */
export type CodingRunTimelineEntry = {
  id: string
  event_type: string
  level: string
  message: string | null
  created_at: string
}

/** A collapsed, human-readable activity line for rendering. */
export type CodingActivityLine = {
  id: string
  level: string
  text: string
  icon: CodingActivityIcon
}

// --------------

/** Map a Claude tool_use into a short "verb target" line + icon (no emoji). */
export function summarizeCodingToolUse(
  name: string,
  input: Record<string, unknown>
): CodingHumanizedEntry {
  const str = (v: unknown) => (typeof v === "string" ? v : "")
  const file = str(input.file_path) || str(input.path) || str(input.notebook_path)
  const shortFile = file ? file.split("/").slice(-2).join("/") : ""

  switch (name) {
    case "Edit":
    case "MultiEdit":
    case "NotebookEdit":
      return { text: `Edited ${shortFile || "a file"}`, icon: "edit" }
    case "Write":
      return { text: `Wrote ${shortFile || "a file"}`, icon: "write" }
    case "Read":
      return { text: `Read ${shortFile || "a file"}`, icon: "read" }
    case "Bash": {
      const cmd = str(input.command).replace(/\s+/g, " ").trim()
      return { text: cmd ? cmd.slice(0, 120) : "Ran a command", icon: "command" }
    }
    case "Grep":
      return { text: `Searched: ${str(input.pattern).slice(0, 80)}`, icon: "search" }
    case "Glob":
      return { text: `Found files: ${str(input.pattern).slice(0, 80)}`, icon: "search" }
    case "WebSearch":
      return { text: `Web search: ${str(input.query).slice(0, 80)}`, icon: "web" }
    case "WebFetch":
      return { text: `Fetched: ${str(input.url).slice(0, 80)}`, icon: "web" }
    case "TodoWrite":
      return { text: "Updated the task plan", icon: "plan" }
    case "Task":
      return { text: `Delegated: ${str(input.description).slice(0, 80)}`, icon: "task" }
    default:
      return { text: `${name}${shortFile ? `: ${shortFile}` : ""}`, icon: "tool" }
  }
}

// --------------

/**
 * Turn a raw timeline entry into a clean, human-readable line + an icon kind
 * (no emoji). Claude Code streams newline-delimited JSON as agent.progress;
 * extract the action (assistant text, or the tool + target) instead of
 * dumping JSON or raw file contents at the user.
 */
export function humanizeCodingTimelineEntry(entry: {
  event_type: string
  message: string | null
}): CodingHumanizedEntry {
  const raw = entry.message?.trim() || entry.event_type

  const LIFECYCLE: Record<string, CodingHumanizedEntry> = {
    "repo.cloning": { text: raw, icon: "repo" },
    "repo.cloned": { text: raw, icon: "repo" },
    "branch.created": { text: raw, icon: "branch" },
    "run.started": { text: "Starting the coding agent", icon: "start" },
    "run.no_changes": { text: "No file changes were needed", icon: "check" },
    "pr.opened": { text: raw, icon: "check" },
    "run.error": { text: "The coding agent hit an error", icon: "error" },
    // Kiro stream-json tool events carry an already-clean "verb target"
    // message; map each to its icon so the timeline shows the right glyph.
    "tool.edit": { text: raw, icon: "edit" },
    "tool.write": { text: raw, icon: "write" },
    "tool.read": { text: raw, icon: "read" },
    "tool.command": { text: raw, icon: "command" },
    "tool.search": { text: raw, icon: "search" },
    "tool.web": { text: raw, icon: "web" },
    "tool.plan": { text: raw, icon: "plan" },
    "tool.progress": { text: raw, icon: "tool" }
  }
  if (LIFECYCLE[entry.event_type] && !raw.startsWith("{")) {
    return LIFECYCLE[entry.event_type]
  }

  if (!raw.startsWith("{")) {
    if (raw.length > 400) return null

    // Diff lines from the agent's edits: highlight add/remove, no icon.
    // Match a leading +/- optionally after a line-number gutter, but not
    // "->" or "--" prose. e.g. "+    3010: ...", "- 3010: content:".
    const addMatch = /^\+(?![+])\s/.test(raw) || /^\+\s*\d+\s*[:|]/.test(raw)
    const removeMatch =
      (/^-(?![->])\s/.test(raw) && !raw.startsWith("- ")) ||
      /^-\s*\d+\s*[:|]/.test(raw)
    if (addMatch) return { text: raw, icon: "diffAdd" }
    if (removeMatch) return { text: raw, icon: "diffRemove" }

    if (entry.level === "error") return { text: raw, icon: "error" }
    // Ordinary prose/progress line — render as plain text, no icon.
    return { text: raw, icon: "plain" }
  }

  // agent.progress carries raw Claude stream JSON — summarize the action.
  try {
    const obj = JSON.parse(raw) as {
      type?: string
      message?: {
        content?: Array<{
          type?: string
          text?: string
          name?: string
          input?: Record<string, unknown>
        }>
      }
      result?: string
    }

    // tool_result echoes (role: user) are noise — skip them.
    if (obj.type === "user") return null

    const blocks = obj.message?.content ?? []
    for (const b of blocks) {
      if (b.type === "tool_use" && b.name) {
        return summarizeCodingToolUse(b.name, b.input ?? {})
      }
    }
    for (const b of blocks) {
      if (b.type === "text" && b.text?.trim()) {
        return { text: b.text.trim().slice(0, 200), icon: "text" }
      }
    }
    if (obj.type === "result" && obj.result?.trim()) {
      return { text: obj.result.trim().slice(0, 200), icon: "text" }
    }
    return null
  } catch {
    return null
  }
}

// --------------

/**
 * Collapse a raw event stream into clean activity lines: drop noise
 * (tool_result echoes, raw file dumps) and consecutive duplicates.
 */
export function buildCodingActivityLines(
  timeline: readonly {
    id: string
    event_type: string
    level: string
    message: string | null
  }[]
): CodingActivityLine[] {
  const out: CodingActivityLine[] = []
  for (const entry of timeline) {
    const h = humanizeCodingTimelineEntry(entry)
    if (!h) continue
    const prev = out[out.length - 1]
    if (prev && prev.text === h.text) continue
    out.push({ id: entry.id, level: entry.level, text: h.text, icon: h.icon })
  }
  return out
}
